import React, { useState, useEffect } from 'react';
import api from '../services/api';
import toast from 'react-hot-toast';
import { Save } from 'lucide-react';

const TABS = ['General','Payment','Delivery','DM Templates'];
const inp = { background:'#f8f9fa', border:'1px solid #dee2e6', borderRadius:8, padding:'9px 13px', fontSize:14, width:'100%', outline:'none', boxSizing:'border-box', color:'#212529' };
const lbl = { display:'block', fontSize:12, fontWeight:600, color:'#6b7280', marginBottom:5, textTransform:'uppercase', letterSpacing:'0.04em' };
const card = { background:'#fff', border:'1px solid #e5e7eb', borderRadius:12, padding:24, marginBottom:20 };
const cardTitle = { fontSize:15, fontWeight:700, color:'#111827', marginBottom:18 };

export default function Settings() {
  const [tab, setTab] = useState('General');
  const [settings, setSettings] = useState({});
  const [saving, setSaving] = useState(false);
  const [changed, setChanged] = useState({});

  useEffect(() => {
    api.get('/settings/').then(r => setSettings(r.data||{})).catch(()=>{});
  }, []);

  const set = (key, val) => {
    setSettings(s => ({...s, [key]: val}));
    setChanged(c => ({...c, [key]: val}));
  };

  const save = async (keys) => {
    setSaving(true);
    try {
      const payload = {};
      keys.forEach(k => { if (settings[k] !== undefined) payload[k] = settings[k]; });
      await api.put('/settings/', payload);
      toast.success('Settings saved');
      setChanged({});
    } catch { toast.error('Failed to save'); }
    finally { setSaving(false); }
  };

  const v = (k, def='') => settings[k] ?? def;

  const tabStyle = (t) => ({
    padding:'8px 18px', border:'none', background:'none', cursor:'pointer',
    fontSize:13, fontWeight:tab===t?700:400, color:tab===t?'#7c6af5':'#6b7280',
    borderBottom:tab===t?'2px solid #7c6af5':'2px solid transparent', marginBottom:-2
  });

  return (
    <div style={{ padding:24, minHeight:'100vh', background:'#f8fafc', maxWidth:760 }}>
      <h1 style={{ margin:'0 0 4px', fontSize:22, fontWeight:700, color:'#111827' }}>⚙️ Settings</h1>
      <p style={{ margin:'0 0 24px', fontSize:13, color:'#6b7280' }}>System configuration for 123 Crystal OMS</p>

      {/* Tabs */}
      <div style={{ display:'flex', borderBottom:'2px solid #e5e7eb', marginBottom:24 }}>
        {TABS.map(t=><button key={t} style={tabStyle(t)} onClick={()=>setTab(t)}>{t}</button>)}
      </div>

      {tab==='General' && (
        <>
          <div style={card}>
            <div style={cardTitle}>🏪 Business Info</div>
            <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:16, marginBottom:16 }}>
              <div><label style={lbl}>Business Name</label><input value={v('business_name')} onChange={e=>set('business_name',e.target.value)} style={inp} /></div>
              <div><label style={lbl}>Business Phone</label><input value={v('business_phone')} onChange={e=>set('business_phone',e.target.value)} style={inp} /></div>
            </div>
            <button onClick={()=>save(['business_name','business_phone'])} disabled={saving} style={{ background:'#7c6af5', color:'#fff', border:'none', borderRadius:8, padding:'9px 20px', fontSize:13, fontWeight:600, cursor:'pointer', display:'flex', alignItems:'center', gap:6 }}><Save size={14}/>{saving?'Saving...':'Save'}</button>
          </div>

          <div style={card}>
            <div style={cardTitle}>⏰ Auto-Cancel Rules</div>
            <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:16, marginBottom:16 }}>
              <div><label style={lbl}>No Contact Auto-Cancel (days)</label><input type="number" value={v('auto_cancel_days','5')} onChange={e=>set('auto_cancel_days',e.target.value)} style={inp} /></div>
              <div><label style={lbl}>Follow-up Highlight (days)</label><input type="number" value={v('followup_days','3')} onChange={e=>set('followup_days',e.target.value)} style={inp} /></div>
            </div>
            <button onClick={()=>save(['auto_cancel_days','followup_days'])} disabled={saving} style={{ background:'#7c6af5', color:'#fff', border:'none', borderRadius:8, padding:'9px 20px', fontSize:13, fontWeight:600, cursor:'pointer', display:'flex', alignItems:'center', gap:6 }}><Save size={14}/>{saving?'Saving...':'Save'}</button>
          </div>
        </>
      )}

      {tab==='Payment' && (
        <>
          <div style={card}>
            <div style={cardTitle}>💱 Exchange Rate</div>
            <div style={{ marginBottom:16 }}>
              <label style={lbl}>USD → KHR Rate (e.g. 4100)</label>
              <input type="number" value={v('usd_khr_rate','4100')} onChange={e=>set('usd_khr_rate',e.target.value)} style={inp} />
              <div style={{ fontSize:12, color:'#9ca3af', marginTop:6 }}>Used for KHR payment conversion. J&T uses their own rate (shown in report).</div>
            </div>
            <button onClick={()=>save(['usd_khr_rate'])} disabled={saving} style={{ background:'#7c6af5', color:'#fff', border:'none', borderRadius:8, padding:'9px 20px', fontSize:13, fontWeight:600, cursor:'pointer', display:'flex', alignItems:'center', gap:6 }}><Save size={14}/>{saving?'Saving...':'Save'}</button>
          </div>

          <div style={card}>
            <div style={cardTitle}>🏦 ABA Bank Info</div>
            <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:16, marginBottom:16 }}>
              <div><label style={lbl}>ABA Account Name</label><input value={v('aba_account_name')} onChange={e=>set('aba_account_name',e.target.value)} style={inp} /></div>
              <div><label style={lbl}>ABA Account Number</label><input value={v('aba_account_number')} onChange={e=>set('aba_account_number',e.target.value)} style={inp} /></div>
            </div>
            <button onClick={()=>save(['aba_account_name','aba_account_number'])} disabled={saving} style={{ background:'#7c6af5', color:'#fff', border:'none', borderRadius:8, padding:'9px 20px', fontSize:13, fontWeight:600, cursor:'pointer', display:'flex', alignItems:'center', gap:6 }}><Save size={14}/>{saving?'Saving...':'Save'}</button>
          </div>
        </>
      )}

      {tab==='Delivery' && (
        <>
          <div style={card}>
            <div style={cardTitle}>📦 Delivery Fees</div>
            <div style={{ marginBottom:16 }}>
              <label style={lbl}>Free Delivery Threshold (USD)</label>
              <input type="number" step="0.5" value={v('free_delivery_threshold','50')} onChange={e=>set('free_delivery_threshold',e.target.value)} style={inp} />
              <div style={{ fontSize:12, color:'#9ca3af', marginTop:6 }}>Orders above this amount get free delivery.</div>
            </div>
            <div style={{ marginBottom:16 }}>
              <label style={lbl}>Default Delivery Fee (USD)</label>
              <input type="number" step="0.5" value={v('delivery_fee_default','2')} onChange={e=>set('delivery_fee_default',e.target.value)} style={inp} />
            </div>
            <button onClick={()=>save(['free_delivery_threshold','delivery_fee_default'])} disabled={saving} style={{ background:'#7c6af5', color:'#fff', border:'none', borderRadius:8, padding:'9px 20px', fontSize:13, fontWeight:600, cursor:'pointer', display:'flex', alignItems:'center', gap:6 }}><Save size={14}/>{saving?'Saving...':'Save'}</button>
          </div>

          <div style={card}>
            <div style={cardTitle}>🛵 DOM Delivery Payout (KHR/pack)</div>
            <div style={{ background:'#f9fafb', borderRadius:8, padding:14, marginBottom:16, fontSize:13, color:'#6b7280' }}>
              Delivery payout is auto-calculated per session based on package count.
            </div>
            <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr 1fr', gap:16, marginBottom:16 }}>
              <div><label style={lbl}>1–10 packages (KHR/pack)</label><input type="number" value={v('delivery_fee_tier1','5000')} onChange={e=>set('delivery_fee_tier1',e.target.value)} style={inp} /></div>
              <div><label style={lbl}>11–20 packages (KHR/pack)</label><input type="number" value={v('delivery_fee_tier2','4500')} onChange={e=>set('delivery_fee_tier2',e.target.value)} style={inp} /></div>
              <div><label style={lbl}>21+ packages (KHR/pack)</label><input type="number" value={v('delivery_fee_tier3','4000')} onChange={e=>set('delivery_fee_tier3',e.target.value)} style={inp} /></div>
            </div>
            <button onClick={()=>save(['delivery_fee_tier1','delivery_fee_tier2','delivery_fee_tier3'])} disabled={saving} style={{ background:'#7c6af5', color:'#fff', border:'none', borderRadius:8, padding:'9px 20px', fontSize:13, fontWeight:600, cursor:'pointer', display:'flex', alignItems:'center', gap:6 }}><Save size={14}/>{saving?'Saving...':'Save'}</button>
          </div>
        </>
      )}

      {tab==='DM Templates' && (
        <>
          <div style={card}>
            <div style={cardTitle}>📨 Order Summary DM</div>
            <div style={{ fontSize:12, color:'#9ca3af', marginBottom:12 }}>
              Variables: <code style={{ background:'#f3f4f6', padding:'1px 6px', borderRadius:4 }}>{'{name}'}</code>{' '}
              <code style={{ background:'#f3f4f6', padding:'1px 6px', borderRadius:4 }}>{'{order_lines}'}</code>{' '}
              <code style={{ background:'#f3f4f6', padding:'1px 6px', borderRadius:4 }}>{'{subtotal}'}</code>{' '}
              <code style={{ background:'#f3f4f6', padding:'1px 6px', borderRadius:4 }}>{'{delivery_fee}'}</code>{' '}
              <code style={{ background:'#f3f4f6', padding:'1px 6px', borderRadius:4 }}>{'{total}'}</code>
            </div>
            <textarea value={v('dm_order_summary')} onChange={e=>set('dm_order_summary',e.target.value)} style={{ ...inp, minHeight:140, resize:'vertical', fontFamily:'inherit', lineHeight:1.6 }} />
            <button onClick={()=>save(['dm_order_summary'])} disabled={saving} style={{ background:'#7c6af5', color:'#fff', border:'none', borderRadius:8, padding:'9px 20px', fontSize:13, fontWeight:600, cursor:'pointer', marginTop:10, display:'flex', alignItems:'center', gap:6 }}><Save size={14}/>{saving?'Saving...':'Save'}</button>
          </div>

          <div style={card}>
            <div style={cardTitle}>💰 Payment Info DM (Province customers)</div>
            <div style={{ fontSize:12, color:'#9ca3af', marginBottom:12 }}>
              Variables: <code style={{ background:'#f3f4f6', padding:'1px 6px', borderRadius:4 }}>{'{aba_account_name}'}</code>{' '}
              <code style={{ background:'#f3f4f6', padding:'1px 6px', borderRadius:4 }}>{'{aba_account_number}'}</code>
            </div>
            <textarea value={v('dm_payment_info')} onChange={e=>set('dm_payment_info',e.target.value)} style={{ ...inp, minHeight:100, resize:'vertical', fontFamily:'inherit', lineHeight:1.6 }} />
            <button onClick={()=>save(['dm_payment_info'])} disabled={saving} style={{ background:'#7c6af5', color:'#fff', border:'none', borderRadius:8, padding:'9px 20px', fontSize:13, fontWeight:600, cursor:'pointer', marginTop:10, display:'flex', alignItems:'center', gap:6 }}><Save size={14}/>{saving?'Saving...':'Save'}</button>
          </div>

          <div style={card}>
            <div style={cardTitle}>📦 Tracking DM</div>
            <div style={{ fontSize:12, color:'#9ca3af', marginBottom:12 }}>
              Variables: <code style={{ background:'#f3f4f6', padding:'1px 6px', borderRadius:4 }}>{'{name}'}</code>{' '}
              <code style={{ background:'#f3f4f6', padding:'1px 6px', borderRadius:4 }}>{'{tracking_no}'}</code>
            </div>
            <textarea value={v('dm_tracking')} onChange={e=>set('dm_tracking',e.target.value)} style={{ ...inp, minHeight:120, resize:'vertical', fontFamily:'inherit', lineHeight:1.6 }} />
            <button onClick={()=>save(['dm_tracking'])} disabled={saving} style={{ background:'#7c6af5', color:'#fff', border:'none', borderRadius:8, padding:'9px 20px', fontSize:13, fontWeight:600, cursor:'pointer', marginTop:10, display:'flex', alignItems:'center', gap:6 }}><Save size={14}/>{saving?'Saving...':'Save'}</button>
          </div>
        </>
      )}
    </div>
  );
}
